import React, { useState, useEffect, useCallback, useRef } from 'react';
import inspectionService from '../../services/inspectionService';
import { INSPECTION_STATUS, INSPECTION_STATUS_LABELS, INSPECTION_STATUS_COLORS, OWNER_DOCUMENT_TYPES, ROLES, VEHICLE_TYPES, toOfficialDocType } from '../../utils/constants';
import { useSpinner } from '../../context/SpinnerContext';
import { useAuthContext } from '../../context/AuthContext';
import ActionConfirmModal from '../ActionConfirmModal/ActionConfirmModal';
import config from '../../config/environment';
import {
    ACCIDENT_TYPE_OPTIONS,
    ARMOR_OPTIONS,
    FUEL_TYPES,
    OFFICIAL_RECORD_FIELDS,
    PLATE_TYPES,
    buildInformationDraft,
    canCorrectInspectionInformation,
    getInformationChanges,
    hasOfficialRecordsConsult,
    informationChangesPayload,
    isDigitalInspection,
    validateInformationDraft,
} from '../../utils/inspectionInformation';
import './InspectionInfo.css';
import logger from '../../utils/logger';

const RESEND_COOLDOWN_SECONDS = 60;

const InspectionInfo = ({ inspectionId, inspectionData, onInspectionUpdate }) => {
    const [inspection, setInspection] = useState(inspectionData || null);
    const [loading, setLoading] = useState(!inspectionData);
    const [error, setError] = useState(null);
    const [correcting, setCorrecting] = useState(false);
    const [draft, setDraft] = useState(null);
    const [correctionError, setCorrectionError] = useState(null);
    const [showCorrectionConfirm, setShowCorrectionConfirm] = useState(false);

    // Email editing and resend states
    const [editingEmail, setEditingEmail] = useState(false);
    const [emailValue, setEmailValue] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendError, setResendError] = useState(null);
    const [resendSuccess, setResendSuccess] = useState(false);
    const cooldownIntervalRef = useRef(null);
    
    const { showSpinner, hideSpinner } = useSpinner();
    const { currentUser } = useAuthContext();

    const loadInspection = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            logger.info('Cargando inspección...', {
                inspectionId: inspectionId,
            }, 'InspectionInfo');
            const result = await inspectionService.getInspection(inspectionId);
            setInspection(result.inspection);
            setEmailValue(result.inspection?.contactData?.email || '');
        } catch (error) {
            logger.error('Error loading inspection:', error, null, 'InspectionInfo');
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [inspectionId]);

    useEffect(() => {
        if (correcting) return;
        if (!inspectionData && inspectionId) {
            loadInspection();
        } else if (inspectionData) {
            setInspection(inspectionData);
            setEmailValue(inspectionData?.contactData?.email || '');
        }
    }, [inspectionId, inspectionData, loadInspection, correcting]);

    useEffect(() => {
        return () => {
            if (cooldownIntervalRef.current) {
                clearInterval(cooldownIntervalRef.current);
            }
        };
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZone: 'America/Bogota'
        });
    };

    const getStatusLabel = (status) => {
        return INSPECTION_STATUS_LABELS[status] || status || 'N/A';
    };

    const getStatusColor = (status) => {
        return INSPECTION_STATUS_COLORS[status] || '#666';
    };

    const renderField = (label, value, control = null) => (
        <div className="info-field">
            <span className="field-label">{label}:</span>
            {control || <span className="field-value">{value || 'N/A'}</span>}
        </div>
    );

    const updateDraft = (key, value) => {
        setDraft((current) => ({ ...current, [key]: value }));
        setCorrectionError(null);
    };

    const fieldControl = (key, config = {}) => {
        if (!correcting || !draft || !Object.prototype.hasOwnProperty.call(draft, key)) return null;
        if (config.options) {
            return (
                <select
                    className="info-edit-input"
                    value={draft[key]}
                    disabled={config.disabled}
                    onChange={(event) => updateDraft(key, event.target.value)}
                >
                    <option value="">Seleccione</option>
                    {config.options.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            );
        }
        return (
            <input
                className="info-edit-input"
                value={draft[key]}
                maxLength={config.maxLength}
                inputMode={config.inputMode}
                disabled={config.disabled}
                onChange={(event) => updateDraft(key, config.transform ? config.transform(event.target.value) : event.target.value)}
            />
        );
    };

    const informationChanges = correcting && inspection && draft
        ? getInformationChanges(inspection, draft)
        : [];
    const informationErrors = correcting && inspection && draft
        ? validateInformationDraft(inspection, draft, { officialRecordsEnabled: config.ENABLE_OFFICIAL_RECORDS })
        : [];
    const canCorrect = canCorrectInspectionInformation(currentUser?.role, inspection);
    const isDigital = isDigitalInspection(inspection);
    const armorOptions = draft?.armor && !ARMOR_OPTIONS.some((option) => option.value === draft.armor)
        ? [...ARMOR_OPTIONS, { value: draft.armor, label: draft.armor }]
        : ARMOR_OPTIONS;

    const startCorrection = () => {
        setDraft(buildInformationDraft(inspection));
        setCorrecting(true);
        setCorrectionError(null);
        setEditingEmail(false);
        setShowCorrectionConfirm(false);
    };

    const cancelCorrection = () => {
        setCorrecting(false);
        setDraft(null);
        setCorrectionError(null);
        setShowCorrectionConfirm(false);
    };

    const requestCorrectionSave = () => {
        if (informationChanges.length === 0 || informationErrors.length > 0) return;
        setShowCorrectionConfirm(true);
    };

    const confirmCorrection = async () => {
        setShowCorrectionConfirm(false);
        showSpinner('Guardando corrección...');
        setCorrectionError(null);
        try {
            const result = await inspectionService.updateInspectionInformation(
                inspection.id,
                informationChangesPayload(informationChanges)
            );
            const updatedInspection = result.inspection;
            setInspection(updatedInspection);
            setEmailValue(updatedInspection?.contactData?.email || '');
            setCorrecting(false);
            setDraft(null);
            if (onInspectionUpdate) onInspectionUpdate(updatedInspection);
            logger.info('Información de la inspección corregida', {
                inspectionId: inspection.id,
                fields: informationChanges.map((change) => change.key),
            }, 'InspectionInfo');
        } catch (saveError) {
            logger.error('Error corrigiendo información:', saveError, null, 'InspectionInfo');
            setCorrectionError(saveError.message || 'Error al guardar la corrección');
        } finally {
            hideSpinner();
        }
    };

    const safeToUpperCase = (value) => {
        if (value === null || value === undefined) return 'N/A';
        if (typeof value === 'boolean') return value ? 'Sí' : 'No';
        if (typeof value === 'number') return value.toString();
        if (typeof value === 'string') return value.toUpperCase();
        return String(value).toUpperCase();
    };

    const getDocTypeLabel = (value) => {
        const officialType = toOfficialDocType(value);
        if (!officialType) return 'N/A';
        const match = OWNER_DOCUMENT_TYPES.find((type) => type.value === officialType);
        return match?.label || officialType;
    };

    const canResendEmail = () => {
        if (!inspection) return false;
        if (inspection.id?.startsWith('DIC-')) return false;
        if (inspection.status === INSPECTION_STATUS.PENDING_USER) return true;
        const hasRejectedPhotos = inspection.photos?.some(photo => 
            photo.rejectionReason && !photo.correctedAt
        );
        return hasRejectedPhotos;
    };

    const canEditEmail = () => {
        return Boolean(inspection && !inspection.id?.startsWith('DIC-'));
    };

    const handleEditEmail = () => {
        setEditingEmail(true);
        setResendError(null);
        setResendSuccess(false);
    };

    const handleCancelEdit = () => {
        setEditingEmail(false);
        setEmailValue(inspection?.contactData?.email || '');
        setResendError(null);
    };

    const handleSaveEmail = () => {
        setEditingEmail(false);
    };

    const startCooldownTimer = () => {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        
        if (cooldownIntervalRef.current) {
            clearInterval(cooldownIntervalRef.current);
        }

        cooldownIntervalRef.current = setInterval(() => {
            setResendCooldown(prev => {
                if (prev <= 1) {
                    clearInterval(cooldownIntervalRef.current);
                    cooldownIntervalRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleResendEmail = async () => {
        if (resendCooldown > 0) return;

        showSpinner('Enviando email...');
        setResendError(null);
        setResendSuccess(false);

        try {
            const currentEmail = inspection?.contactData?.email?.toLowerCase();
            const newEmailValue = emailValue.trim().toLowerCase();
            const emailChanged = newEmailValue !== currentEmail;

            const result = await inspectionService.resendEmail(
                inspection.id,
                emailChanged ? newEmailValue : null
            );

            logger.info('Email reenviado exitosamente', {
                inspectionId: inspection.id,
                emailChanged,
                sentTo: result.sentTo
            }, 'InspectionInfo');

            setResendSuccess(true);
            startCooldownTimer();
            setEditingEmail(false);

            if (emailChanged && onInspectionUpdate) {
                const updatedInspection = {
                    ...inspection,
                    contactData: {
                        ...inspection.contactData,
                        email: newEmailValue
                    }
                };
                setInspection(updatedInspection);
                onInspectionUpdate(updatedInspection);
            }

            setTimeout(() => setResendSuccess(false), 5000);

        } catch (error) {
            logger.error('Error reenviando email:', error, null, 'InspectionInfo');
            
            const errorData = error.response?.data;
            if (errorData?.remainingSeconds) {
                setResendCooldown(errorData.remainingSeconds);
                startCooldownTimer();
            }
            
            setResendError(errorData?.error || error.message || 'Error al reenviar el email');
        } finally {
            hideSpinner();
        }
    };

    const renderEmailField = () => {
        const showResendButton = canResendEmail();
        const showEmailActions = canEditEmail() && !correcting;
        const currentEmail = inspection?.contactData?.email;

        return (
            <div className="info-field email-field-container">
                <span className="field-label">Email:</span>
                <div className="email-field-content">
                    {editingEmail && showEmailActions ? (
                        <input
                            type="email"
                            className="email-input"
                            value={emailValue}
                            onChange={(e) => setEmailValue(e.target.value)}
                            placeholder="correo@ejemplo.com"
                        />
                    ) : (
                        <span className="field-value email-value">
                            {safeToUpperCase(currentEmail)}
                        </span>
                    )}
                    
                    {showEmailActions && (
                    <div className="email-actions">
                        {editingEmail ? (
                            <>
                                <button
                                    className="email-action-btn save-btn"
                                    onClick={handleSaveEmail}
                                    title="Guardar"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                </button>
                                <button
                                    className="email-action-btn cancel-btn"
                                    onClick={handleCancelEdit}
                                    title="Cancelar"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </>
                        ) : (
                            <button
                                className="email-action-btn edit-btn"
                                onClick={handleEditEmail}
                                title="Editar email"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                            </button>
                        )}

                        {showResendButton && (
                            <button
                                className={`resend-btn ${resendCooldown > 0 ? 'cooldown' : ''}`}
                                onClick={handleResendEmail}
                                disabled={resendCooldown > 0}
                                title={resendCooldown > 0 ? `Esperar ${resendCooldown}s` : 'Reenviar email'}
                            >
                                {resendCooldown > 0 ? (
                                    <>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <polyline points="12 6 12 12 16 14"></polyline>
                                        </svg>
                                        <span>{resendCooldown}s</span>
                                    </>
                                ) : (
                                    <>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                            <polyline points="22,6 12,13 2,6"></polyline>
                                        </svg>
                                        <span>Reenviar</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    )}
                </div>

                {resendError && (
                    <div className="resend-message error">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                        {resendError}
                    </div>
                )}

                {resendSuccess && (
                    <div className="resend-message success">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        Email reenviado exitosamente
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="inspection-info-loading">
                <div className="loading-spinner"></div>
                <p>Cargando información de la inspección...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="inspection-info-error">
                <p>Error: {error}</p>
                <button onClick={loadInspection} className="btn btn-primary">
                    Reintentar
                </button>
            </div>
        );
    }

    if (!inspection) {
        return (
            <div className="inspection-info-empty">
                <p>No se encontró información de la inspección</p>
            </div>
        );
    }

    const recordsWarning = hasOfficialRecordsConsult(inspection)
        && informationChanges.some((change) => OFFICIAL_RECORD_FIELDS.includes(change.key));

    return (
        <div className="inspection-info">
            {canCorrect && (
                <div className="info-correction-bar">
                    <p className="info-correction-note">
                        {correcting
                            ? 'Corrige los datos y confirma. El cambio queda en los comentarios.'
                            : currentUser?.role === ROLES.ADMINISTRATOR
                                ? 'Puedes corregir los datos de la ficha en cualquier estado. El cambio queda en los comentarios.'
                                : 'Puedes corregir los datos de la ficha mientras la inspección no esté finalizada.'}
                    </p>
                    <div className="info-correction-actions">
                        {correcting ? (
                            <>
                                <button type="button" className="info-correction-btn secondary" onClick={cancelCorrection}>
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className="info-correction-btn primary"
                                    onClick={requestCorrectionSave}
                                    disabled={informationChanges.length === 0 || informationErrors.length > 0}
                                >
                                    Guardar correcciones
                                </button>
                            </>
                        ) : (
                            <button type="button" className="info-correction-btn primary" onClick={startCorrection}>
                                Corregir información
                            </button>
                        )}
                    </div>
                </div>
            )}
            {correcting && informationErrors.length > 0 && (
                <div className="info-correction-errors">
                    {informationErrors.map((message) => <p key={message}>{message}</p>)}
                </div>
            )}
            {correctionError && (
                <div className="info-correction-errors">
                    <p>{correctionError}</p>
                </div>
            )}
            {/* Datos de Inspección */}
            <div className="info-section">
                <h3 className="inspection-info-section-title">
                    <svg className="inspection-info-section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4"></path>
                        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"></path>
                    </svg>
                    Datos de Inspección
                </h3>
                <div className="info-grid">
                    {renderField('ID de Inspección', safeToUpperCase(inspection.id))}
                    {renderField('Fecha de Creación', formatDate(inspection.createdAt))}
                    {renderField('Última Actualización', formatDate(inspection.updatedAt))}
                    <div className="info-field">
                        <span className="field-label">Estado:</span>
                        <span
                            className="field-value status-badge"
                            style={{ backgroundColor: getStatusColor(inspection.status) }}
                        >
                            {getStatusLabel(inspection.status)}
                        </span>
                    </div>
                    {renderField('Creado por', safeToUpperCase(inspection.createdBy?.name))}
                    {renderField('Empresa', safeToUpperCase(inspection.partnerData?.insuranceCompany))}
                    {(inspection.partnerData?.intermediary || (correcting && isDigital)) && renderField(
                        'Intermediario',
                        safeToUpperCase(inspection.partnerData?.intermediary),
                        fieldControl('intermediary', { maxLength: 80, transform: (value) => value.toUpperCase() })
                    )}
                    {inspection.token && renderField('Token de Cliente', inspection.token)}
                </div>
            </div>

            {/* Información de Siniestro */}
            {inspection.isAccident && (
                <div className="info-section">
                    <h3 className="inspection-info-section-title">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                        Información de Siniestro
                    </h3>
                    <div className="info-grid">
                        {renderField('Es Siniestro', 'Sí')}
                        {(inspection.accidentType || correcting) && renderField(
                            'Tipo de Siniestro',
                            inspection.accidentType === 'own' ? 'Propio' : inspection.accidentType === 'third_party' ? 'Tercero' : 'N/A',
                            fieldControl('accidentType', { options: ACCIDENT_TYPE_OPTIONS })
                        )}
                    </div>
                </div>
            )}

            {/* Datos del Vehículo */}
            <div className="info-section">
                <h3 className="inspection-info-section-title">
                    <svg className="inspection-info-section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                        <line x1="8" y1="21" x2="16" y2="21"></line>
                        <line x1="12" y1="17" x2="12" y2="21"></line>
                    </svg>
                    Datos del Vehículo
                </h3>
                <div className="info-grid">
                    {renderField('Placa', safeToUpperCase(inspection.vehicleData?.licensePlate), fieldControl('licensePlate', {
                        maxLength: 12,
                        transform: (value) => value.replace(/\s+/g, '').toUpperCase(),
                    }))}
                    {renderField('Tipo de Placa', safeToUpperCase(inspection.vehicleData?.plateType), fieldControl('plateType', { options: PLATE_TYPES }))}
                    {renderField('Año Modelo', safeToUpperCase(inspection.vehicleData?.year), fieldControl('year', {
                        maxLength: 4,
                        inputMode: 'numeric',
                        transform: (value) => value.replace(/\D/g, '').slice(0, 4),
                    }))}
                    {renderField('Tipo de Vehículo', safeToUpperCase(inspection.vehicleData?.type), fieldControl('type', { options: VEHICLE_TYPES }))}
                    {renderField('Marca', safeToUpperCase(inspection.vehicleData?.brand), fieldControl('brand', { maxLength: 60, transform: (value) => value.toUpperCase() }))}
                    {renderField('Línea', safeToUpperCase(inspection.vehicleData?.line), fieldControl('line', { maxLength: 60, transform: (value) => value.toUpperCase() }))}
                    {(inspection.vehicleData?.vin || !isDigital) && renderField('VIN', safeToUpperCase(inspection.vehicleData?.vin), fieldControl('vin', {
                        maxLength: 17,
                        transform: (value) => value.replace(/[\s-]/g, '').toUpperCase(),
                    }))}
                    {renderField(inspection.vehicleData?.vin || !isDigital ? 'Chasis' : 'Chasis o VIN', safeToUpperCase(inspection.vehicleData?.chassis), fieldControl('chassis', { maxLength: 30, transform: (value) => value.toUpperCase() }))}
                    {!isDigital && renderField('Motor', safeToUpperCase(inspection.vehicleData?.engine), fieldControl('engine', { maxLength: 30, transform: (value) => value.toUpperCase() }))}
                    {renderField('Combustible', safeToUpperCase(inspection.vehicleData?.fuel), fieldControl('fuel', { options: FUEL_TYPES }))}
                    {renderField('Blindaje', safeToUpperCase(inspection.vehicleData?.armor), fieldControl('armor', {
                        options: armorOptions,
                        disabled: draft?.type === 'MOTOCICLETA',
                    }))}
                    {renderField('Cod. Faseco', safeToUpperCase(inspection.vehicleData?.fasecoCode), fieldControl('fasecoCode', { maxLength: 20, transform: (value) => value.toUpperCase() }))}
                </div>
            </div>

            {/* Datos de Contacto */}
            <div className="info-section">
                <h3 className="inspection-info-section-title">
                    <svg className="inspection-info-section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    Datos de Contacto
                </h3>
                <div className="info-grid">
                    {renderField('Nombre del propietario', safeToUpperCase(inspection.contactData?.name), fieldControl('name', { maxLength: 120 }))}
                    {renderField('Tipo de documento', getDocTypeLabel(inspection.contactData?.docType), fieldControl('docType', { options: OWNER_DOCUMENT_TYPES }))}
                    {renderField('Número de documento', inspection.contactData?.docNumber || 'N/A', fieldControl('docNumber', { maxLength: 20 }))}
                    {renderField('Teléfono', safeToUpperCase(inspection.contactData?.phone), fieldControl('phone', { maxLength: 20 }))}
                    {renderEmailField()}
                </div>
            </div>
            <ActionConfirmModal
                isOpen={showCorrectionConfirm}
                onClose={() => setShowCorrectionConfirm(false)}
                onConfirm={confirmCorrection}
                title="Confirmar corrección"
                confirmText="Guardar corrección"
                cancelText="Volver"
                message={(
                    <div className="correction-confirm">
                        <ul className="correction-diff-list">
                            {informationChanges.map((change) => (
                                <li key={change.key}>
                                    <strong>{change.label}:</strong> {change.beforeDisplay} → {change.afterDisplay}
                                </li>
                            ))}
                        </ul>
                        {recordsWarning && (
                            <p className="correction-records-warning">
                                Los antecedentes oficiales se consultaron con los datos anteriores y no se actualizan con esta corrección. Si aplica, vuelve a consultarlos.
                            </p>
                        )}
                    </div>
                )}
            />
        </div>
    );
};

export default InspectionInfo;
